import React, { useEffect, useState, useRef } from "react";
import Switch from "@mui/material/Switch";
import { green, red } from "@mui/material/colors";
import {
  getEmpresasRequest,
  createEmpresaRequest,
  updateEmpresaRequest,
  enableEmpresa,
  disableEmpresa,
} from "../services/empresas/empresasData";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Paper,
  Container,
  Chip,
  InputAdornment,
  TableContainer,
  TablePagination
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ImageIcon from '@mui/icons-material/Image';
import BusinessIcon from '@mui/icons-material/Business';
import imageCompression from 'browser-image-compression';
import { toast } from "react-toastify";

const EmpresaModal = ({
  open,
  onClose,
  onSubmit,
  initialData = { emp_nombre: "", emp_descripcion: "", emp_campo: "", emp_logo: "" },
  isEdit = false,
}) => {
  const [form, setForm] = useState({
    ...initialData,
    emp_logo: (initialData?.emp_logo && typeof initialData.emp_logo === 'string') ? initialData.emp_logo : "",
  });
  const fileInputRef = useRef();

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("La imagen es demasiado grande. Máximo 2MB.");
        return;
      }
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 800,
        useWebWorker: true
      };
      try {
        const compressedFile = await imageCompression(file, options);
        const reader = new FileReader();
        reader.onloadend = () => {
          setForm({ ...form, emp_logo: reader.result });
        };
        reader.readAsDataURL(compressedFile);
      } catch (error) {
        toast.error("Error al procesar la imagen.");
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setForm({ ...form, emp_logo: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  useEffect(() => {
    setForm({
      ...initialData,
      emp_logo: (initialData?.emp_logo && typeof initialData.emp_logo === 'string') ? initialData.emp_logo : "",
    });
  }, [initialData, open]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = () => {
    if (!form.emp_nombre.trim()) {
      toast.warning("El nombre de la empresa es obligatorio.");
      return;
    }
    onSubmit(form);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#001F3F' }}>
          {isEdit ? "Editar Empresa" : "Crear Nueva Empresa"}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Nombre de la Empresa"
            name="emp_nombre"
            value={form.emp_nombre}
            onChange={handleChange}
            fullWidth
            variant="outlined"
            required
          />
          <TextField
            label="Descripción"
            name="emp_descripcion"
            value={form.emp_descripcion}
            onChange={handleChange}
            fullWidth
            multiline
            rows={3}
            variant="outlined"
          />
          <TextField
            label="Campo / Sector"
            name="emp_campo"
            value={form.emp_campo}
            onChange={handleChange}
            fullWidth
            variant="outlined"
          />

          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            sx={{
              border: "2px dashed #ccc",
              borderRadius: 1,
              p: 3,
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: "#fafafa",
              '&:hover': {
                backgroundColor: '#f5f5f5',
                borderColor: '#999'
              }
            }}
            onClick={() => fileInputRef.current.click()}
          >
            {form.emp_logo && (typeof form.emp_logo === 'string' ? form.emp_logo.trim() !== "" : true) ? (
              <Box>
                <img src={form.emp_logo} alt="Logo" style={{ maxHeight: 100, borderRadius: 4, marginBottom: 8 }} />
                <Typography variant="caption" display="block" sx={{ color: '#666' }}>
                  Clic para cambiar imagen
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <ImageIcon sx={{ fontSize: 40, mb: 1, color: '#999' }} />
                <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                  Logo de la Empresa
                </Typography>
                <Typography variant="caption" sx={{ color: '#999' }}>
                  Arrastra una imagen o haz clic para seleccionar
                </Typography>
              </Box>
            )}
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              ref={fileInputRef}
              onChange={handleFileChange}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ color: '#666' }}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            backgroundColor: '#001F3F',
            color: '#fff',
            '&:hover': { 
              backgroundColor: '#003366',
            },
          }}
        >
          {isEdit ? "Guardar Cambios" : "Crear Empresa"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Empresas = () => {
  const [empresas, setEmpresas] = useState([]);
  const [filteredEmpresas, setFilteredEmpresas] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editEmpresa, setEditEmpresa] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const emptyEmpresa = { emp_nombre: "", emp_descripcion: "", emp_campo: "", emp_logo: "" };
  const initialEmpresaData = editEmpresa || emptyEmpresa;

  const fetchEmpresas = async () => {
    try {
      const empresasData = await getEmpresasRequest();
      const data = Array.isArray(empresasData) ? empresasData : [];
      setEmpresas(data);
      setFilteredEmpresas(data);
    } catch (error) {
      console.error("Error al obtener empresas:", error);
      toast.error("Error al cargar las empresas.");
      setEmpresas([]);
      setFilteredEmpresas([]);
    }
  };

  useEffect(() => {
    fetchEmpresas();
  }, []);

  useEffect(() => {
    const results = empresas.filter(empresa =>
      empresa.emp_nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      empresa.emp_campo.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredEmpresas(results);
    setPage(0);
  }, [searchTerm, empresas]);

  const handleCreate = () => {
    setEditEmpresa(null);
    setModalOpen(true);
  };

  const handleEdit = (empresa) => {
    setEditEmpresa(empresa);
    setModalOpen(true);
  };

  const handleToggleHabilitado = async (empresa) => {
    try {
      if (empresa.emp_status) {
        await disableEmpresa(empresa.emp_codigo);
        toast.success("Empresa deshabilitada correctamente.");
      } else {
        await enableEmpresa(empresa.emp_codigo);
        toast.success("Empresa habilitada correctamente.");
      }
      fetchEmpresas();
    } catch (error) {
      toast.error("Error al cambiar el estado de habilitación.");
    }
  };

  const handleModalSubmit = async (data) => {
    const payload = {
      ...data,
      emp_logo: data?.emp_logo && (typeof data.emp_logo === 'string' ? data.emp_logo.trim() !== "" : data.emp_logo) ? data.emp_logo : "",
    };

    try {
      if (editEmpresa) {
        await updateEmpresaRequest(editEmpresa.emp_codigo, payload);
        toast.success("Empresa actualizada exitosamente.");
      } else {
        await createEmpresaRequest(payload);
        toast.success("Empresa creada exitosamente.");
      }
      setModalOpen(false);
      fetchEmpresas();
    } catch (error) {
      console.error("Error al guardar empresa:", error);
      toast.error("Error al guardar la empresa. Intente nuevamente.");
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calcular estadísticas
  const totalEmpresas = empresas.length;
  const empresasActivas = empresas.filter(e => e.emp_status).length;
  const empresasInactivas = totalEmpresas - empresasActivas;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" fontWeight="600" sx={{ color: '#001F3F' }}>
          Gestión de Empresas
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          sx={{
            backgroundColor: '#001F3F',
            color: '#fff',
            '&:hover': { 
              backgroundColor: '#003366',
            },
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          Nueva Empresa
        </Button>
      </Box>

      <Paper 
        elevation={0}
        sx={{ 
          width: '100%', 
          mb: 2, 
          border: '1px solid #e0e0e0',
        }}
      >
        {/* Toolbar con Búsqueda */}
        <Box 
          sx={{ 
            p: 2, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            borderBottom: '1px solid #e0e0e0',
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <TextField
            variant="outlined"
            size="small"
            placeholder="Buscar empresa..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: '100%', sm: 300 } }}
          />
          <Typography variant="body2" sx={{ color: '#666' }}>
            {filteredEmpresas.length} {filteredEmpresas.length === 1 ? 'resultado' : 'resultados'}
          </Typography>
        </Box>

        <TableContainer>
          <Table sx={{ minWidth: 750 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Logo</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Nombre</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Descripción</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Sector</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#001F3F' }} align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEmpresas
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((empresa) => (
                  <TableRow
                    key={empresa.emp_codigo}
                    hover
                    sx={{ 
                      '&:last-child td, &:last-child th': { border: 0 },
                    }}
                  >
                    <TableCell>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#f5f5f5',
                          border: '1px solid #e0e0e0'
                        }}
                      >
                        {empresa.emp_logo ? (
                          <img src={empresa.emp_logo} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <BusinessIcon sx={{ color: '#999', fontSize: 20 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight="500" sx={{ color: '#001F3F' }}>
                        {empresa.emp_nombre}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#666',
                          maxWidth: 300, 
                          whiteSpace: 'nowrap', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis' 
                        }}
                      >
                        {empresa.emp_descripcion || 'Sin descripción'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={empresa.emp_campo || "General"}
                        size="small"
                        sx={{ 
                          backgroundColor: '#f5f5f5', 
                          color: '#666',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={empresa.emp_status ? "Activo" : "Inactivo"}
                        size="small"
                        sx={{
                          backgroundColor: empresa.emp_status ? '#e8f5e9' : '#f5f5f5',
                          color: empresa.emp_status ? '#2e7d32' : '#666',
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <IconButton
                          onClick={() => handleEdit(empresa)}
                          size="small"
                          sx={{ color: "#666" }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <Switch
                          checked={!!empresa.emp_status}
                          onChange={() => handleToggleHabilitado(empresa)}
                          size="small"
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: green[600],
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: green[600],
                            },
                          }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              {filteredEmpresas.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" sx={{ color: '#666' }}>
                      No se encontraron empresas
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredEmpresas.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
        />
      </Paper>

      {/* Modal para crear/editar */}
      <EmpresaModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleModalSubmit}
        initialData={initialEmpresaData}
        isEdit={!!editEmpresa}
      />
    </Container>
  );
};

export { Empresas };